import moment from 'moment';
import toast from 'react-hot-toast';
import { takeEvery, takeLatest, put, call, select, take } from 'redux-saga/effects';
import * as seriesApi from '../../api/seriesApi';
import * as actions from '../actions';
import * as selectors from './../selectors';
import { push } from 'connected-react-router';

const isActive = result => {
  const today = moment();
  if (!moment.isMoment(result.data.startDate)) return false;
  if (!moment.isMoment(result.data.endDate)) return false;
  return today.isBetween(result?.data?.startDate, result?.data?.endDate, '[]');
};

const normalizeSeriesData = (data = {}) => ({
  ...data,
  startDate: data.startDate ?? data.start_date,
  endDate: data.endDate ?? data.end_date,
  homePage: data.homePage ?? data.home_page,
  hideDates: data.hideDates ?? data.hide_dates,
  sponsorLink: data.sponsorLink ?? data.sponsor_link,
  sponsorName: data.sponsorName ?? data.sponsor_name,
  digestSeries: data.digestSeries ?? data.digest_series,
  digestHeaderImage: data.digestHeaderImage ?? data.digest_header_image,
  digestLogoImage: data.digestLogoImage ?? data.digest_logo_image,
  is_active: data.is_active,
});

export function* getSeries({ payload: { siteId } }) {
  try {
    let sites = yield select(selectors.admin.sites);

    if (sites.length === 0) {
      yield take(actions.admin.getSitesSuccess);
      sites = yield select(selectors.admin.sites);
    }

    // yield put(actions.user.setLoading(true, 'Retrieving series'));

    const mapToSeriesRecords = (records, site_id) =>
      records
        .map(result => {
          const data = normalizeSeriesData(result.data);
          return {
            ...result,
            site_id,
            data: {
              ...data,
              startDate: data?.startDate ? moment(data.startDate, 'YYYY-MM-DD').format('LL') : '',
              timestamp: data?.startDate ? moment(data.startDate, 'YYYY-MM-DD').unix() : 0,
              endDate: data?.endDate ? moment(data.endDate, 'YYYY-MM-DD').format('LL') : '',
            },
          };
        })
        .map(result => ({
          ...result,
          data: {
            ...result.data,
            isActive: isActive(result),
          },
        }));

    let results = [];
    if (siteId !== 'All') {
      results = yield call(seriesApi.getSeries, siteId);
      results = mapToSeriesRecords(results, siteId);
    } else {
      for (const site of sites.filter(s => s.property === 'CultureMap')) {
        const records = yield call(seriesApi.getSeries, site.id);
        results = [...results, ...mapToSeriesRecords(records, site.id)];
      }
    }

    yield put(actions.series.getSeriesSuccess(results, siteId));
  } catch (e) {
    console.log(e);
    yield put(actions.series.getSeriesFail(e));
    toast.error('An error occurred retrieving the series.');
  } finally {
    yield put(actions.user.setLoading(false));
  }
}
export function* getSeriesPosts({ payload: { siteId, seriesId } }) {
  try {
    yield put(actions.series.getSeriesPostsSuccess([]));
    yield put(actions.user.setLoading(true, 'Retrieving posts for series'));
    const results = yield call(seriesApi.getPostsForSeries, { siteId, seriesId });
    yield put(actions.series.getSeriesPostsSuccess(results));
  } catch (e) {
    yield put(actions.series.getSeriesPostsFail([]));
    toast.error('An error occurred retrieving the series.');
  } finally {
    yield put(actions.user.setLoading(false));
  }
}
export function* addPostToSeries({ payload: { siteId, seriesId, postId } }) {
  try {
    yield put(actions.user.setLoading(true, 'Updating series'));
    yield call(seriesApi.addPostToSeries, { siteId, seriesId, postId });
    yield put(actions.user.setLoading(false));
    yield put(actions.series.getSeriesPosts(siteId, seriesId));
    yield put(actions.series.addPostToSeriesSuccess());
  } catch (e) {
    yield put(actions.series.addPostToSeriesFail());
    toast.error('An error occurred updating the series.');
    yield put(actions.user.setLoading(false));
  }
}
export function* addSeries({ payload: { series } }) {
  try {
    yield put(actions.user.setLoading(true, 'Creating series'));
    const newSeries = yield call(seriesApi.addSeries, series);

    yield put(actions.series.getSeries(series.siteId));
    yield put(push(`/series/${series.siteId}/edit/${newSeries.id}`));
    yield put(actions.series.addSeriesSuccess());
    toast.success('Series created.');
    yield put(actions.user.setLoading(false));
  } catch (e) {
    yield put(actions.series.addSeriesFail());
    toast.error('An error occurred creating the series.');
    yield put(actions.user.setLoading(false));
  }
}
export function* removePostFromSeries({ payload: { siteId, seriesId, postId } }) {
  try {
    yield put(actions.user.setLoading(true, 'Updating series'));
    yield call(seriesApi.removePostFromSeries, { siteId, seriesId, postId });
    yield put(actions.user.setLoading(false));
    yield put(actions.series.getSeriesPosts(siteId, seriesId));
    yield put(actions.series.removePostFromSeriesSuccess());
  } catch (e) {
    yield put(actions.series.removePostFromSeriesFail());
    toast.error('An error occurred updating the series.');
    yield put(actions.user.setLoading(false));
  }
}

export function* updateSeries({ payload: { siteId, series } }) {
  try {
    yield put(actions.user.setLoading(true, 'Updating series'));
    const toDate = date => (date ? moment(date).format('YYYY-MM-DD') : '');
    const { site_id, ...seriesData } = series;
    const data = normalizeSeriesData(series.data || {});
    delete data.isActive;
    const updateData = {
      ...data,
      startDate: toDate(data?.startDate),
      endDate: toDate(data?.endDate),
    };
    updateData.start_date = updateData.startDate;
    updateData.end_date = updateData.endDate;
    updateData.home_page = updateData.homePage;
    updateData.hide_dates = updateData.hideDates;
    updateData.sponsor_link = updateData.sponsorLink;
    updateData.sponsor_name = updateData.sponsorName;
    updateData.digest_series = updateData.digestSeries;
    updateData.digest_header_image = updateData.digestHeaderImage;
    updateData.digest_logo_image = updateData.digestLogoImage;
    if (updateData.is_active === undefined) {
      delete updateData.is_active;
    }
    const update = {
      ...seriesData,
      title: seriesData.data.name,
      data: updateData,
    };

    //remove null properties from update.data
    Object.keys(update.data).forEach(key => {
      if (update.data[key] === null) {
        delete update.data[key];
      }
    });

    const results = yield call(seriesApi.updateSeries, { siteId, series: update });

    const [updatedSeries] = [results]

      .map(result => {
        const data = normalizeSeriesData(result.data);
        return {
          ...result,
          site_id,
          data: {
            ...data,
            startDate: data?.startDate ? moment(data.startDate, 'YYYY-MM-DD').format('LL') : '',
            timestamp: data?.startDate ? moment(data.startDate, 'YYYY-MM-DD').unix() : 0,
            endDate: data?.endDate ? moment(data.endDate, 'YYYY-MM-DD').format('LL') : '',
          },
        };
      })
      .map(result => ({
        ...result,
        data: {
          ...result.data,
          isActive: isActive(result),
        },
      }));

    yield put(actions.series.updateSeriesSuccess(updatedSeries));
    // yield put(push(`/series`));
    toast.success('Series updated.');
  } catch (e) {
    console.log(e);
    yield put(actions.series.updateSeriesFail());
    toast.error('An error occurred updating the series.');
  } finally {
    yield put(actions.user.setLoading(false));
  }
}

export function* seriesSagas() {
  yield takeLatest(actions.series.getSeries, getSeries);
  yield takeEvery(actions.series.getSeriesPosts, getSeriesPosts);
  yield takeEvery(actions.series.addPostToSeries, addPostToSeries);
  yield takeEvery(actions.series.removePostFromSeries, removePostFromSeries);
  yield takeEvery(actions.series.updateSeries, updateSeries);
  yield takeEvery(actions.series.addSeries, addSeries);
}
